import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { UseIsHighOrLowTouchTwcSignupResult } from './useIsHighOrLowTouchTwcSignup.ts';
import type { UseLoomNudgeDailyLimitResult } from './useLoomNudgeDailyLimit.ts';
import type { UseMeetingRecorderEligibilityResult } from './useMeetingRecorderEligibility.ts';

type UseMeetingSignalErrorAnalyticsOptions = {
	tapTraits: Pick<UseIsHighOrLowTouchTwcSignupResult, 'loading' | 'error'>;
	dailyLimit: Pick<UseLoomNudgeDailyLimitResult, 'loading' | 'error'>;
	meetingRecorderEligibility: Pick<UseMeetingRecorderEligibilityResult, 'error'>;
	skip: boolean;
};

// Meeting-signal prerequisites are read by multiple surfaces during one app lifecycle.
let hasFiredPrerequisiteErrorEvent = false;

export function resetPrerequisiteErrorEventForTest(): void {
	hasFiredPrerequisiteErrorEvent = false;
}

/** Fires one operational event containing the resolved meeting-signal prerequisite errors. */
export function useMeetingSignalErrorAnalytics({
	tapTraits,
	dailyLimit,
	meetingRecorderEligibility,
	skip,
}: UseMeetingSignalErrorAnalyticsOptions): void {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasTapTraitsError = tapTraits.error !== null;
	const hasDailyLimitError = dailyLimit.error !== undefined;
	const hasMeetingRecorderEligibilityError = meetingRecorderEligibility.error !== null;

	useEffect(() => {
		if (
			skip ||
			tapTraits.loading ||
			dailyLimit.loading ||
			hasFiredPrerequisiteErrorEvent ||
			(!hasTapTraitsError && !hasDailyLimitError && !hasMeetingRecorderEligibilityError)
		) {
			return;
		}

		hasFiredPrerequisiteErrorEvent = true;
		createAnalyticsEvent({
			eventType: 'operational',
			action: 'failed',
			actionSubject: 'loomMeetingNudge',
			source: 'loomMeetingNudgeError',
			attributes: {
				tapTraitsError: hasTapTraitsError,
				dailyLimitError: hasDailyLimitError,
				meetingRecorderEligibilityError: hasMeetingRecorderEligibilityError,
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		dailyLimit.loading,
		hasDailyLimitError,
		hasMeetingRecorderEligibilityError,
		hasTapTraitsError,
		skip,
		tapTraits.loading,
	]);
}
